<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue';
import {contacts} from "@js/company_details.ts";
import {useDeliveryStore} from '@js/store/DeliveryStore.js';
import {checkDocument, formatPhone} from "@js/helpers.ts";
import {getAddressSuggestions} from "@js/yandex-api.ts";
import IconManipulatorCar1 from '@js/components/Icons/IconManipulatorCar1.vue';
import IconManipulatorCar2 from '@js/components/Icons/IconManipulatorCar2.vue';
import IconExclamationFilled from "@js/components/Icons/IconExclamationFilled.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import IconMixerCar1 from "@js/components/Icons/IconMixerCar1.vue";

const formatedPhone = computed(() => formatPhone(contacts.sales_department_phone));

const delivery = useDeliveryStore();
const dropdownRef = ref(null);

// ТЕПЕРЬ ВСЕГДА манипулятор, 6м и 12м
const vehiclesList = computed(() => delivery.vehicles.manipulator);

// одна иконка на оба варианта
const carIconMap = {
    '6m': IconManipulatorCar2,
    '12m': IconManipulatorCar1,
    'mixer': IconMixerCar1,
};
const carIconFor = (id) => carIconMap[id] ?? IconManipulatorCar1;

function marketRound(x) {
    const ceil10 = Math.ceil(x / 10) * 10;
    return Math.max(0, ceil10 - 1);
}

function formatRUB(n) {
    return new Intl.NumberFormat('ru-RU').format(Math.round(n)) + ' ₽';
}

const priceInDay = computed(() => {
    const k = delivery.timeMultipliers.in_day;
    const val = delivery.baseInDayZone1 * (delivery.zone?.coeff ?? 1) * k;
    return marketRound(val);
});

const priceExact = computed(() => {
    const k = delivery.timeMultipliers.exact;
    const val = delivery.baseInDayZone1 * (delivery.zone?.coeff ?? 1) * k;
    return marketRound(val);
});

let debounce = null;
const suggestions = ref([]);
const query = ref(delivery.address);

const fetchSuggestions = () => {
    clearTimeout(debounce);
    debounce = setTimeout(async () => {
        if ((query.value || '').length < 3) {
            suggestions.value = [];
            return;
        }
        suggestions.value = await getAddressSuggestions(query.value);
    }, 300);
};

const selectSuggestion = (value, lat, lon) => {
    query.value = value;
    delivery.address = value;
    suggestions.value = [];
    delivery.setCoords([lat, lon]);
};

const handleClickOutside = (event) => {
    if (!dropdownRef.value?.contains(event.target)) {
        suggestions.value = [];
    }
};

onMounted(() => {
    if (checkDocument()) {
        document.addEventListener('click', handleClickOutside);
    }
});

onUnmounted(() => {
    if (checkDocument()) {
        document.removeEventListener('click', handleClickOutside);
    }
});
</script>

<template>
    <div class="w-full">
        <h2 class="text-2xl md:text-3xl font-bold mb-2">Расчет доставки</h2>

        <!-- Адрес -->
        <div class="mb-4">
            <label class="block text-sm text-gray-600 mb-1">Адрес или координаты доставки</label>
            <div>
                <div ref="dropdownRef" class="relative mb-4">
                    <input v-model="query" @input="fetchSuggestions" type="text"
                           placeholder="Адрес доставки" autocomplete="off"
                           class="w-full mt-3 px-4 py-2 mb-1 border border-gray-300 focus:border-baseColor focus:ring-baseColor rounded-md"/>
                    <ul v-if="suggestions.length"
                        class="absolute z-10 w-full bg-white border rounded shadow mt-1 max-h-60 overflow-y-auto">
                        <li v-for="(suggestion, i) in suggestions" :key="i"
                            @click="selectSuggestion(suggestion.label, suggestion.lat, suggestion.lon)"
                            class="px-4 py-2 md:hover:bg-gray-100 cursor-pointer text-sm">
                            {{ suggestion.label }}
                        </li>
                    </ul>
                </div>
            </div>
            <p class="text-xs text-gray-500 mt-1">
                Для расчёта стоимости доставки введите адрес текстом или выберите на карте
            </p>
        </div>

        <!-- Выбор авто (только манипулятор 6м/12м) -->
        <div class="mb-6">
            <div class="flex gap-4 overflow-x-auto pb-1 -mx-2 px-2 md:mx-0 md:px-0 md:grid md:grid-cols-5">
                <div v-for="v in vehiclesList" :key="v.id"
                     class="flex-none w-[95px] text-center md:flex-1 md:w-auto snap-start">
                    <button
                        @click="delivery.setVehicle(v.id)"
                        class="h-12 w-24 md:h-14 md:w-full rounded-md border flex items-center justify-center transition shadow-sm overflow-hidden"
                        :class="delivery.vehicle === v.id ? 'border-black text-black' : 'border-gray-300 text-gray-400 hover:text-gray-600'"
                        :aria-pressed="delivery.vehicle === v.id"
                        :title="`Макс. длина: ${v.maxLenM} м`">
                        <component :is="carIconFor(v.id)"
                                   class="h-full w-full"
                                   :class="delivery.vehicle === v.id ? 'opacity-100' : 'opacity-70'"/>
                    </button>
                    <span class="block mt-2 text-xs md:text-sm text-nowrap"
                          :class="delivery.vehicle === v.id ? 'font-semibold text-gray-900' : 'text-gray-400'">
            {{ v.label }}
          </span>
                </div>
            </div>
        </div>

        <template v-if="delivery.out_of_zones">
            <div class="flex items-center gap-2 border-2 border-amber-500 p-3 rounded">
                <IconExclamationFilled class="h-4 w-4 md:h-7 md:w-7 flex-shrink-0 text-amber-500"/>
                <p class="text-[13px] md:text-sm text-gray-800 font-medium">
                    Доставка по указанному адресу возможна только по договорной цене, звоните:
                    <br class="max-md:hidden"/>
                    <a :href="'tel:+'+contacts.sales_department_phone"
                       class="font-semibold underline hover:no-underline">
                        {{ formatedPhone }}
                    </a>.
                </p>
            </div>
        </template>
        <template v-else>
            <h3 class="text-sm uppercase tracking-wide text-gray-500 mb-3">Цены на доставку</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-[15px]">
                <div class="flex items-center justify-between border-b py-2">
                    <div class="flex items-center gap-2">
                        <span class="font-medium">В течение дня</span>
                    </div>
                    <div class="font-semibold text-gray-900">
                        <NumberFormatComponent :number="priceInDay" append_text=" ₽" :decimals="0"/>
                    </div>
                </div>
                <div class="flex items-center justify-between border-b py-2">
                    <div class="flex items-center gap-2">
                        <span class="font-medium">Точно ко времени</span>
                    </div>
                    <div class="font-semibold text-gray-900">
                        <NumberFormatComponent :number="priceExact" append_text=" ₽" :decimals="0"/>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
